/* =============================================
   COLLECTION FILTERS WITH METAFIELD SUPPORT
   ============================================= */

(function() {
  'use strict';

  // Collection Filter Manager
  const CollectionFilters = {
    products: [],
    activeFilters: {
      price: { min: null, max: null },
      vendor: [],
      type: [],
      tag: [],
      metafields: {}
    },
    currentSort: 'manual',
    filterConfig: null,

    init: function() {
      // Check if we're on a collection page
      if (!document.getElementById('productGrid')) return;

      this.loadFilterConfig();
      this.collectProductData();
      this.setupEventListeners();
      this.extractMetafields();
      this.renderMetafieldFilters();
    },

    // Load filter configuration from collection metafield
    loadFilterConfig: function() {
      const productGrid = document.getElementById('productGrid');
      const configData = productGrid?.dataset.filterConfig;
      
      if (configData) {
        try {
          this.filterConfig = JSON.parse(configData);
          console.log('Filter config loaded:', this.filterConfig);
        } catch (e) {
          console.error('Failed to parse filter config:', e);
          this.filterConfig = null;
        }
      }
    },

    // Collect product data from DOM
    collectProductData: function() {
      const productItems = document.querySelectorAll('.product-item');
      this.products = Array.from(productItems).map(item => {
        const productData = {
          element: item,
          id: item.dataset.productId,
          price: parseFloat(item.dataset.price) || 0,
          vendor: item.dataset.vendor || '',
          type: item.dataset.type || '',
          tags: (item.dataset.tags || '').split(',').filter(t => t.trim()),
          title: item.dataset.title || '',
          created: parseInt(item.dataset.created) || 0,
          metafields: this.extractProductMetafields(item)
        };
        console.log('Product collected:', productData.title, 'metafields:', productData.metafields);
        return productData;
      });
      console.log('Total products collected:', this.products.length);
    },

    // Extract metafields from product element
    extractProductMetafields: function(element) {
      const metafields = {};
      
      // Get all data attributes that start with 'data-meta-'
      Array.from(element.attributes).forEach(attr => {
        if (attr.name.startsWith('data-meta-')) {
          const key = attr.name.replace('data-meta-', '');
          let value = attr.value;
          
          // Try to parse as JSON (for complex metafields like weight)
          try {
            const parsed = JSON.parse(value);
            if (parsed && typeof parsed === 'object' && parsed.value !== undefined) {
              // This is a complex metafield with value and possibly unit
              const numValue = parseFloat(parsed.value);
              metafields[key] = {
                value: numValue,
                unit: parsed.unit || '',
                type: 'number',
                rawValue: parsed.unit ? `${parsed.value} ${parsed.unit}` : String(parsed.value),
                displayValue: parsed.unit ? `${parsed.value} ${parsed.unit}` : String(parsed.value)
              };
              console.log(`Parsed JSON metafield ${key}:`, numValue, parsed.unit);
              return;
            }
          } catch (e) {
            // Not JSON, continue with regular parsing
          }
          
          // Extract numeric value from string (handles cases like "5100 kg")
          const numericMatch = value.match(/^([0-9.,-]+)\s*(.*)$/);
          if (numericMatch) {
            const numStr = numericMatch[1].replace(',', '.');
            const numValue = parseFloat(numStr);
            const unit = numericMatch[2].trim();
            
            if (!isNaN(numValue)) {
              metafields[key] = {
                value: numValue,
                unit: unit,
                type: 'number',
                rawValue: value,
                displayValue: value
              };
              console.log(`Parsed numeric metafield ${key}:`, numValue, unit, 'from', value);
              return;
            }
          }
          
          // Fallback: treat as string
          metafields[key] = {
            value: value,
            type: 'string',
            rawValue: value,
            displayValue: value
          };
          console.log(`String metafield ${key}:`, value);
        }
      });
      
      return metafields;
    },

    // Extract unique metafields from all products based on config
    extractMetafields: function() {
      const metafieldMap = new Map();

      // Without config, show no dynamic filters
      if (!this.filterConfig || !this.filterConfig.tags) {
        this.metafields = [];
        return;
      }

      // Use configured metafields — optional showInFilter flag, fallback: show all
      this.filterConfig.tags.forEach(tagConfig => {
        // Skip if explicitly excluded from filter
        if (tagConfig.showInFilter === false) return;

        const key = tagConfig.key;

        this.products.forEach(product => {
          const metafieldData = product.metafields[key];
          if (metafieldData) {
            if (!metafieldMap.has(key)) {
              metafieldMap.set(key, {
                key: key,
                type: metafieldData.type,
                unit: tagConfig.postfix || metafieldData.unit || '',
                name: tagConfig.name || this.formatMetafieldTitle(key),
                filterMode: tagConfig.filter?.mode || (metafieldData.type === 'number' ? 'range' : 'checkbox'),
                values: new Set()
              });
            }
            metafieldMap.get(key).values.add(metafieldData.rawValue);
          }
        });
      });

      this.metafields = Array.from(metafieldMap.values());
    },

    // Render dynamic metafield filters
    renderMetafieldFilters: function() {
      const container = document.getElementById('dynamicFilters');
      if (!container || this.metafields.length === 0) return;

      this.metafields.forEach(metafield => {
        const filterGroup = document.createElement('div');
        filterGroup.className = 'filter-group';

        const title = document.createElement('h3');
        title.className = 'filter-group-title';
        title.textContent = metafield.name || this.formatMetafieldTitle(metafield.key);
        filterGroup.appendChild(title);

        // Use configured filter mode or auto-detect
        if (metafield.filterMode === 'range' && metafield.values.size > 1) {
          // Check if all values are numeric
          const allNumeric = Array.from(metafield.values).every(v => {
            const numValue = String(v).replace(/[^0-9.-]/g, '');
            return !isNaN(numValue) && numValue.trim() !== '';
          });
          
          if (allNumeric) {
            const rangeFilter = this.createRangeFilter(metafield);
            filterGroup.appendChild(rangeFilter);
          } else {
            const checkboxFilter = this.createCheckboxFilter(metafield);
            filterGroup.appendChild(checkboxFilter);
          }
        } else {
          // Create checkbox filter for non-numeric or single-value metafields
          const checkboxFilter = this.createCheckboxFilter(metafield);
          filterGroup.appendChild(checkboxFilter);
        }

        container.appendChild(filterGroup);
      });
    },

    // Create range filter for numeric metafields
    createRangeFilter: function(metafield) {
      // Calculate actual min/max from product data
      // Extract numeric values, removing units if present
      const values = Array.from(metafield.values).map(v => {
        const numValue = String(v).replace(/[^0-9.-]/g, '');
        return parseFloat(numValue);
      }).filter(v => !isNaN(v));
      
      const minValue = Math.min(...values);
      const maxValue = Math.max(...values);
      
      // Determine appropriate step based on value types
      // Check if all values are integers or have decimals
      const hasDecimals = values.some(v => v % 1 !== 0);
      const step = hasDecimals ? 0.1 : 1;

      const rangeDiv = document.createElement('div');
      rangeDiv.className = 'filter-range';

      // Create slider HTML with unit support
      const unit = metafield.unit ? ` ${metafield.unit}` : '';
      rangeDiv.innerHTML = `
        <div class="price-slider-wrapper">
          <div class="price-slider-values">
            <span class="price-value" id="meta-${metafield.key}-min-display">${Math.round(minValue * 10) / 10}${unit}</span>
            <span class="price-value" id="meta-${metafield.key}-max-display">${Math.round(maxValue * 10) / 10}${unit}</span>
          </div>
          <div class="price-slider-container">
            <input 
              type="range" 
              id="meta-${metafield.key}-min" 
              class="price-slider price-slider-min" 
              min="${minValue}" 
              max="${maxValue}" 
              value="${minValue}" 
              step="${step}"
              data-metafield-key="${metafield.key}"
              data-unit="${metafield.unit || ''}"
            >
            <input 
              type="range" 
              id="meta-${metafield.key}-max" 
              class="price-slider price-slider-max" 
              min="${minValue}" 
              max="${maxValue}" 
              value="${maxValue}" 
              step="${step}"
              data-metafield-key="${metafield.key}"
              data-unit="${metafield.unit || ''}"
            >
            <div class="slider-track"></div>
            <div class="slider-range" id="meta-${metafield.key}-slider-range"></div>
          </div>
        </div>
      `;

      // Setup slider functionality
      setTimeout(() => {
        const minSlider = document.getElementById(`meta-${metafield.key}-min`);
        const maxSlider = document.getElementById(`meta-${metafield.key}-max`);
        const sliderRange = document.getElementById(`meta-${metafield.key}-slider-range`);
        const minDisplay = document.getElementById(`meta-${metafield.key}-min-display`);
        const maxDisplay = document.getElementById(`meta-${metafield.key}-max-display`);

        if (minSlider && maxSlider) {
          const unit = minSlider.dataset.unit || '';
          const unitSuffix = unit ? ` ${unit}` : '';
          
          const updateSlider = () => {
            let min = parseFloat(minSlider.value);
            let max = parseFloat(maxSlider.value);

            // Prevent sliders from crossing
            if (min >= max) {
              if (minSlider === document.activeElement) {
                maxSlider.value = min;
                max = min;
              } else {
                minSlider.value = max;
                min = max;
              }
            }

            // Update display values with unit
            minDisplay.textContent = Math.round(min * 10) / 10 + unitSuffix;
            maxDisplay.textContent = Math.round(max * 10) / 10 + unitSuffix;

            // Update slider range visual
            const percentMin = ((min - minValue) / (maxValue - minValue)) * 100;
            const percentMax = ((max - minValue) / (maxValue - minValue)) * 100;
            sliderRange.style.left = percentMin + '%';
            sliderRange.style.width = (percentMax - percentMin) + '%';

            // Apply filters only if values differ from defaults
            const hasMinFilter = min > minValue;
            const hasMaxFilter = max < maxValue;
            
            if (hasMinFilter || hasMaxFilter) {
              if (!this.activeFilters.metafields[metafield.key]) {
                this.activeFilters.metafields[metafield.key] = {};
              }

              this.activeFilters.metafields[metafield.key] = {
                type: 'range',
                min: hasMinFilter ? min : null,
                max: hasMaxFilter ? max : null
              };
            } else {
              // Remove filter if both sliders are at default position
              delete this.activeFilters.metafields[metafield.key];
            }

            this.applyFilters();
          };

          minSlider.addEventListener('input', updateSlider.bind(this));
          maxSlider.addEventListener('input', updateSlider.bind(this));

          // Initialize slider range visual only (without triggering filter)
          // Display values are already set correctly in the HTML
          sliderRange.style.left = '0%';
          sliderRange.style.width = '100%';
        }
      }, 0);

      return rangeDiv;
    },

    // Create checkbox filter for non-numeric metafields
    createCheckboxFilter: function(metafield) {
      const optionsDiv = document.createElement('div');
      optionsDiv.className = 'filter-options';

      const sortedValues = Array.from(metafield.values).sort();

      sortedValues.forEach(value => {
        const label = document.createElement('label');
        label.className = 'filter-checkbox';

        const input = document.createElement('input');
        input.type = 'checkbox';
        input.name = `metafield-${metafield.key}`;
        input.value = value;
        input.dataset.filterType = 'metafield';
        input.dataset.metafieldKey = metafield.key;

        const span = document.createElement('span');
        span.className = 'filter-label';
        span.textContent = value;

        label.appendChild(input);
        label.appendChild(span);
        optionsDiv.appendChild(label);
      });

      return optionsDiv;
    },

    // Format metafield key to readable title
    formatMetafieldTitle: function(key) {
      return key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase());
    },

    // Setup event listeners
    setupEventListeners: function() {
      // Filter toggle (mobile)
      const filtersToggle = document.getElementById('filtersToggle');
      const filtersContent = document.getElementById('filtersContent');
      
      if (filtersToggle && filtersContent) {
        filtersToggle.addEventListener('click', () => {
          filtersContent.classList.toggle('active');
        });
      }

      // Clear all filters
      const clearFilters = document.getElementById('clearFilters');
      if (clearFilters) {
        clearFilters.addEventListener('click', () => this.clearAllFilters());
      }

      // Price slider setup
      const priceMinSlider = document.getElementById('priceMin');
      const priceMaxSlider = document.getElementById('priceMax');
      const sliderRange = document.getElementById('sliderRange');
      const minPriceDisplay = document.getElementById('minPriceDisplay');
      const maxPriceDisplay = document.getElementById('maxPriceDisplay');

      if (priceMinSlider && priceMaxSlider) {
        // Calculate max price from products
        const maxPrice = Math.max(...this.products.map(p => p.price), 10000);
        priceMinSlider.max = maxPrice;
        priceMaxSlider.max = maxPrice;
        priceMaxSlider.value = maxPrice;
        maxPriceDisplay.textContent = `€${Math.round(maxPrice)}`;

        const updateSlider = () => {
          let minVal = parseFloat(priceMinSlider.value);
          let maxVal = parseFloat(priceMaxSlider.value);

          // Prevent sliders from crossing
          if (minVal >= maxVal) {
            if (priceMinSlider === document.activeElement) {
              priceMaxSlider.value = minVal;
              maxVal = minVal;
            } else {
              priceMinSlider.value = maxVal;
              minVal = maxVal;
            }
          }

          // Update display values
          minPriceDisplay.textContent = `€${Math.round(minVal)}`;
          maxPriceDisplay.textContent = `€${Math.round(maxVal)}`;

          // Update slider range visual
          const percentMin = (minVal / maxPrice) * 100;
          const percentMax = (maxVal / maxPrice) * 100;
          sliderRange.style.left = percentMin + '%';
          sliderRange.style.width = (percentMax - percentMin) + '%';

          // Apply filters only if values differ from defaults
          const hasMinFilter = minVal > 0;
          const hasMaxFilter = maxVal < maxPrice;
          
          this.activeFilters.price.min = hasMinFilter ? minVal : null;
          this.activeFilters.price.max = hasMaxFilter ? maxVal : null;
          this.applyFilters();
        };

        priceMinSlider.addEventListener('input', updateSlider);
        priceMaxSlider.addEventListener('input', updateSlider);

        // Initialize slider range visual only (without triggering filter)
        const percentMin = 0;
        const percentMax = 100;
        sliderRange.style.left = percentMin + '%';
        sliderRange.style.width = (percentMax - percentMin) + '%';
      }

      // Checkbox filters (vendor, type, tag, metafields)
      document.addEventListener('change', (e) => {
        if (e.target.type === 'checkbox' && e.target.dataset.filterType) {
          this.handleCheckboxChange(e.target);
        }
      });

      // Sort
      const sortSelect = document.getElementById('sortBy');
      if (sortSelect) {
        sortSelect.addEventListener('change', (e) => {
          this.currentSort = e.target.value;
          this.applyFilters();
        });
      }
    },

    // Handle checkbox change
    handleCheckboxChange: function(checkbox) {
      const filterType = checkbox.dataset.filterType;
      const value = checkbox.value;

      if (filterType === 'metafield') {
        const metafieldKey = checkbox.dataset.metafieldKey;
        
        if (!this.activeFilters.metafields[metafieldKey]) {
          this.activeFilters.metafields[metafieldKey] = {
            type: 'checkbox',
            values: []
          };
        }

        if (checkbox.checked) {
          if (!this.activeFilters.metafields[metafieldKey].values.includes(value)) {
            this.activeFilters.metafields[metafieldKey].values.push(value);
          }
        } else {
          this.activeFilters.metafields[metafieldKey].values = 
            this.activeFilters.metafields[metafieldKey].values.filter(v => v !== value);
        }
      } else {
        if (checkbox.checked) {
          if (!this.activeFilters[filterType].includes(value)) {
            this.activeFilters[filterType].push(value);
          }
        } else {
          this.activeFilters[filterType] = this.activeFilters[filterType].filter(v => v !== value);
        }
      }

      this.applyFilters();
    },


    // Apply all filters
    applyFilters: function() {
      let filteredProducts = [...this.products];

      // Price filter
      if (this.activeFilters.price.min !== null || this.activeFilters.price.max !== null) {
        filteredProducts = filteredProducts.filter(product => {
          const price = product.price;
          const minOk = this.activeFilters.price.min === null || price >= this.activeFilters.price.min;
          const maxOk = this.activeFilters.price.max === null || price <= this.activeFilters.price.max;
          return minOk && maxOk;
        });
      }

      // Vendor filter
      if (this.activeFilters.vendor.length > 0) {
        filteredProducts = filteredProducts.filter(product => 
          this.activeFilters.vendor.includes(product.vendor)
        );
      }

      // Type filter
      if (this.activeFilters.type.length > 0) {
        filteredProducts = filteredProducts.filter(product => 
          this.activeFilters.type.includes(product.type)
        );
      }

      // Tag filter
      if (this.activeFilters.tag.length > 0) {
        filteredProducts = filteredProducts.filter(product => 
          this.activeFilters.tag.some(tag => product.tags.includes(tag))
        );
      }

      // Metafield filters
      Object.entries(this.activeFilters.metafields).forEach(([key, filter]) => {
        if (filter.type === 'range') {
          if (filter.min !== null || filter.max !== null) {
            console.log(`Applying range filter for ${key}:`, filter);
            filteredProducts = filteredProducts.filter(product => {
              const metafield = product.metafields[key];
              if (!metafield) {
                console.log(`Product ${product.id} has no metafield ${key}`);
                return false;
              }
              if (metafield.type !== 'number') {
                console.log(`Product ${product.id} metafield ${key} is not numeric:`, metafield);
                return false;
              }
              
              const value = parseFloat(metafield.value);
              const minOk = filter.min === null || value >= filter.min;
              const maxOk = filter.max === null || value <= filter.max;
              const passes = minOk && maxOk;
              
              console.log(`Product ${product.id} (${product.title}): ${key}=${value}, min=${filter.min}, max=${filter.max}, passes=${passes}`);
              return passes;
            });
          }
        } else if (filter.type === 'checkbox' && filter.values.length > 0) {
          filteredProducts = filteredProducts.filter(product => {
            const metafield = product.metafields[key];
            if (!metafield) return false;
            return filter.values.includes(metafield.rawValue);
          });
        }
      });

      // Sort products
      this.sortProducts(filteredProducts);

      // Update display
      this.updateProductDisplay(filteredProducts);
      this.updateActiveFiltersDisplay();
      this.updateProductCount(filteredProducts.length);
    },

    // Sort products
    sortProducts: function(products) {
      switch (this.currentSort) {
        case 'price-asc':
          products.sort((a, b) => a.price - b.price);
          break;
        case 'price-desc':
          products.sort((a, b) => b.price - a.price);
          break;
        case 'title-asc':
          products.sort((a, b) => a.title.localeCompare(b.title));
          break;
        case 'title-desc':
          products.sort((a, b) => b.title.localeCompare(a.title));
          break;
        case 'created-desc':
          products.sort((a, b) => b.created - a.created);
          break;
        case 'created-asc':
          products.sort((a, b) => a.created - b.created);
          break;
        default:
          // Keep original order (manual)
          break;
      }
    },

    // Update product display
    updateProductDisplay: function(filteredProducts) {
      const productGrid = document.getElementById('productGrid');
      if (!productGrid) return;

      // Hide all products first
      this.products.forEach(product => {
        product.element.classList.add('hidden');
      });

      // Show filtered products in sorted order
      filteredProducts.forEach((product, index) => {
        product.element.classList.remove('hidden');
        product.element.style.order = index;
      });

      // Animate products
      setTimeout(() => {
        filteredProducts.forEach(product => {
          product.element.style.animation = 'fadeInUp 0.4s ease-out';
        });
      }, 50);
    },

    // Update active filters display
    updateActiveFiltersDisplay: function() {
      const container = document.getElementById('activeFilters');
      const clearButton = document.getElementById('clearFilters');
      
      if (!container) return;

      container.innerHTML = '';
      const tags = [];

      // Price filter tag
      if (this.activeFilters.price.min !== null || this.activeFilters.price.max !== null) {
        const min = this.activeFilters.price.min ? Math.round(this.activeFilters.price.min) : 0;
        const max = this.activeFilters.price.max ? Math.round(this.activeFilters.price.max) : '∞';
        tags.push(this.createFilterTag('Preis', `€${min} - €${max}`, () => {
          this.activeFilters.price = { min: null, max: null };
          const priceMinSlider = document.getElementById('priceMin');
          const priceMaxSlider = document.getElementById('priceMax');
          const minPriceDisplay = document.getElementById('minPriceDisplay');
          const maxPriceDisplay = document.getElementById('maxPriceDisplay');
          const sliderRange = document.getElementById('sliderRange');
          
          if (priceMinSlider && priceMaxSlider) {
            const maxPrice = parseFloat(priceMaxSlider.max);
            priceMinSlider.value = 0;
            priceMaxSlider.value = maxPrice;
            minPriceDisplay.textContent = '€0';
            maxPriceDisplay.textContent = `€${Math.round(maxPrice)}`;
            sliderRange.style.left = '0%';
            sliderRange.style.width = '100%';
          }
          this.applyFilters();
        }));
      }

      // Vendor, type, tag filters
      ['vendor', 'type', 'tag'].forEach(filterType => {
        this.activeFilters[filterType].forEach(value => {
          const label = this.formatFilterLabel(filterType);
          tags.push(this.createFilterTag(label, value, () => {
            this.removeFilter(filterType, value);
          }));
        });
      });

      // Metafield filters
      Object.entries(this.activeFilters.metafields).forEach(([key, filter]) => {
        // Find the metafield config to get the proper name
        const metafieldConfig = this.metafields.find(m => m.key === key);
        const label = metafieldConfig?.name || this.formatMetafieldTitle(key);
        
        if (filter.type === 'range' && (filter.min !== null || filter.max !== null)) {
          const minSlider = document.getElementById(`meta-${key}-min`);
          const maxSlider = document.getElementById(`meta-${key}-max`);
          const actualMin = minSlider ? parseFloat(minSlider.min) : 0;
          const actualMax = maxSlider ? parseFloat(maxSlider.max) : '∞';
          const unit = minSlider && minSlider.dataset.unit ? ` ${minSlider.dataset.unit}` : '';
          
          const displayMin = filter.min !== null ? (Math.round(filter.min * 10) / 10) : actualMin;
          const displayMax = filter.max !== null ? (Math.round(filter.max * 10) / 10) : actualMax;
          
          tags.push(this.createFilterTag(label, `${displayMin}${unit} - ${displayMax}${unit}`, () => {
            this.removeMetafieldFilter(key, null);
          }));
        } else if (filter.type === 'checkbox' && filter.values.length > 0) {
          filter.values.forEach(value => {
            tags.push(this.createFilterTag(label, value, () => {
              this.removeMetafieldFilter(key, value);
            }));
          });
        }
      });

      if (tags.length > 0) {
        tags.forEach(tag => container.appendChild(tag));
        container.style.display = 'flex';
        if (clearButton) clearButton.style.display = 'block';
      } else {
        container.style.display = 'none';
        if (clearButton) clearButton.style.display = 'none';
      }

      this.savePdpChipHighlights();
    },

    // Werte der aktiven Filter für Hervorhebung passender Metafeld-Chips auf der Produktdetailseite
    savePdpChipHighlights: function() {
      const values = [];
      ['vendor', 'type', 'tag'].forEach((filterType) => {
        this.activeFilters[filterType].forEach((v) => {
          if (v) values.push(String(v).trim());
        });
      });
      Object.values(this.activeFilters.metafields).forEach((filter) => {
        if (filter.type === 'checkbox' && filter.values && filter.values.length > 0) {
          filter.values.forEach((v) => {
            if (v) values.push(String(v).trim());
          });
        }
      });
      try {
        if (values.length > 0) {
          sessionStorage.setItem('ownThemePdpChips', JSON.stringify(values));
        } else {
          sessionStorage.removeItem('ownThemePdpChips');
        }
      } catch (e) {
        /* Quota oder private mode */
      }
    },

    // Create filter tag
    createFilterTag: function(label, value, onRemove) {
      const tag = document.createElement('div');
      tag.className = 'active-filter-tag';
      
      const text = document.createElement('span');
      text.textContent = `${label}: ${value}`;
      
      const button = document.createElement('button');
      button.innerHTML = '×';
      button.addEventListener('click', onRemove);
      
      tag.appendChild(text);
      tag.appendChild(button);
      
      return tag;
    },

    // Format filter label
    formatFilterLabel: function(type) {
      const labels = {
        vendor: 'Hersteller',
        type: 'Typ',
        tag: 'Tag'
      };
      return labels[type] || type;
    },

    // Remove filter
    removeFilter: function(filterType, value) {
      this.activeFilters[filterType] = this.activeFilters[filterType].filter(v => v !== value);
      
      // Uncheck corresponding checkbox
      const checkbox = document.querySelector(`input[name="${filterType}"][value="${value}"]`);
      if (checkbox) checkbox.checked = false;
      
      this.applyFilters();
    },

    // Remove metafield filter
    removeMetafieldFilter: function(key, value) {
      if (this.activeFilters.metafields[key]) {
        if (this.activeFilters.metafields[key].type === 'checkbox') {
          this.activeFilters.metafields[key].values = 
            this.activeFilters.metafields[key].values.filter(v => v !== value);
          
          // Uncheck corresponding checkbox
          const checkbox = document.querySelector(
            `input[data-metafield-key="${key}"][value="${value}"]`
          );
          if (checkbox) checkbox.checked = false;
        } else if (this.activeFilters.metafields[key].type === 'range') {
          // Reset range filter
          delete this.activeFilters.metafields[key];
          
          // Reset sliders
          const minSlider = document.getElementById(`meta-${key}-min`);
          const maxSlider = document.getElementById(`meta-${key}-max`);
          const minDisplay = document.getElementById(`meta-${key}-min-display`);
          const maxDisplay = document.getElementById(`meta-${key}-max-display`);
          const sliderRange = document.getElementById(`meta-${key}-slider-range`);
          
          if (minSlider && maxSlider) {
            const minValue = parseFloat(minSlider.min);
            const maxValue = parseFloat(maxSlider.max);
            const unit = minSlider.dataset.unit || '';
            const unitSuffix = unit ? ` ${unit}` : '';
            minSlider.value = minValue;
            maxSlider.value = maxValue;
            minDisplay.textContent = Math.round(minValue * 10) / 10 + unitSuffix;
            maxDisplay.textContent = Math.round(maxValue * 10) / 10 + unitSuffix;
            sliderRange.style.left = '0%';
            sliderRange.style.width = '100%';
          }
        }
        
        this.applyFilters();
      }
    },

    // Update product count
    updateProductCount: function(count) {
      const countElement = document.getElementById('productCount');
      if (countElement) {
        countElement.textContent = `${count} ${count === 1 ? 'Produkt' : 'Produkte'}`;
      }
    },

    // Clear all filters
    clearAllFilters: function() {
      // Reset active filters
      this.activeFilters = {
        price: { min: null, max: null },
        vendor: [],
        type: [],
        tag: [],
        metafields: {}
      };

      // Uncheck all checkboxes
      document.querySelectorAll('.filter-checkbox input[type="checkbox"]').forEach(cb => {
        cb.checked = false;
      });

      // Reset price sliders
      const priceMinSlider = document.getElementById('priceMin');
      const priceMaxSlider = document.getElementById('priceMax');
      const minPriceDisplay = document.getElementById('minPriceDisplay');
      const maxPriceDisplay = document.getElementById('maxPriceDisplay');
      const sliderRange = document.getElementById('sliderRange');
      
      if (priceMinSlider && priceMaxSlider) {
        const maxPrice = parseFloat(priceMaxSlider.max);
        priceMinSlider.value = 0;
        priceMaxSlider.value = maxPrice;
        minPriceDisplay.textContent = '€0';
        maxPriceDisplay.textContent = `€${Math.round(maxPrice)}`;
        sliderRange.style.left = '0%';
        sliderRange.style.width = '100%';
      }

      // Reset all metafield sliders
      document.querySelectorAll('.price-slider').forEach(slider => {
        if (slider.id.startsWith('meta-') && slider.dataset.metafieldKey) {
          const key = slider.dataset.metafieldKey;
          const minSlider = document.getElementById(`meta-${key}-min`);
          const maxSlider = document.getElementById(`meta-${key}-max`);
          const minDisplay = document.getElementById(`meta-${key}-min-display`);
          const maxDisplay = document.getElementById(`meta-${key}-max-display`);
          const sliderRange = document.getElementById(`meta-${key}-slider-range`);
          
          if (minSlider && maxSlider && !minSlider.dataset.reset) {
            const minValue = parseFloat(minSlider.min);
            const maxValue = parseFloat(maxSlider.max);
            const unit = minSlider.dataset.unit || '';
            const unitSuffix = unit ? ` ${unit}` : '';
            minSlider.value = minValue;
            maxSlider.value = maxValue;
            if (minDisplay) minDisplay.textContent = Math.round(minValue * 10) / 10 + unitSuffix;
            if (maxDisplay) maxDisplay.textContent = Math.round(maxValue * 10) / 10 + unitSuffix;
            if (sliderRange) {
              sliderRange.style.left = '0%';
              sliderRange.style.width = '100%';
            }
            minSlider.dataset.reset = 'true';
          }
        }
      });
      
      // Clean up reset markers
      document.querySelectorAll('.price-slider').forEach(slider => {
        delete slider.dataset.reset;
      });

      // Reset sort
      const sortSelect = document.getElementById('sortBy');
      if (sortSelect) {
        sortSelect.value = 'manual';
        this.currentSort = 'manual';
      }

      this.applyFilters();
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => CollectionFilters.init());
  } else {
    CollectionFilters.init();
  }

  // Expose to window for debugging
  window.CollectionFilters = CollectionFilters;

})();

